import React from 'react';
import { User } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/Card';
import { profileLanguageOptions, type Errors } from '../types';

interface ProfileInfoSectionProps {
  bio: string;
  jobTitle: string;
  location: string;
  companyNameOverride: string;
  emailOverride: string;
  phoneOverride: string;
  profileLanguage: string;
  errors: Errors;
  onInputChange: (field: string, value: string) => void;
  t: (key: string) => string;
}

const ProfileInfoSection: React.FC<ProfileInfoSectionProps> = ({
  bio,
  jobTitle,
  location,
  companyNameOverride,
  emailOverride,
  phoneOverride,
  profileLanguage,
  errors,
  onInputChange,
  t
}) => {
  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <User className="w-5 h-5 me-2" />
          {t('Profile Information')}
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('Bio')}
            </label>
            <textarea
              value={bio}
              onChange={(e) => onInputChange('bio', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              placeholder={t('Tell visitors about yourself...')}
            />
            {errors?.bio && <p className="text-sm text-destructive mt-1">{errors.bio}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('Custom Job Title')}
            </label>
            <input
              type="text"
              value={jobTitle}
              onChange={(e) => onInputChange('jobTitle', e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              placeholder={t('Override your job title for public display')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('Custom Location')}
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => onInputChange('location', e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              placeholder={t('Where are you based?')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('Custom Company Name')}
            </label>
            <input
              type="text"
              value={companyNameOverride}
              onChange={(e) => onInputChange('companyNameOverride', e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              placeholder={t('Override the company name shown on your public profile')}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('Leave empty to use your real company name.')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('Custom Email')}
            </label>
            <input
              type="email"
              value={emailOverride}
              onChange={(e) => onInputChange('emailOverride', e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              placeholder={t('e.g. contact@yourdomain.com')}
            />
            {errors?.emailOverride && <p className="text-sm text-destructive mt-1">{errors.emailOverride}</p>}
            <p className="text-xs text-muted-foreground mt-1">
              {t('Only shown when "Show Email" is enabled. Leave empty to use your real email.')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('Custom Phone')}
            </label>
            <input
              type="tel"
              value={phoneOverride}
              onChange={(e) => onInputChange('phoneOverride', e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              placeholder={t('e.g. +1 555 000 0000')}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('Only shown when "Show Phone" is enabled. Leave empty to use your real phone number.')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2" htmlFor="profile-language">
              {t('Profile Language')}
            </label>
            <select
              id="profile-language"
              data-testid="profile-language-select"
              value={profileLanguage}
              onChange={(e) => onInputChange('profileLanguage', e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            >
              {profileLanguageOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {t(option.label)}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              {t('Your public profile will always render in this language. Visitors cannot switch it.')}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfileInfoSection;
